import React, { useState } from "react";
import { Alert, Button, Collapse, DatePicker, Form, Input, InputNumber, Select, Spin, Table, Tooltip } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";
import { useLazyFetchProcurementReportQuery } from "features/store";

const { Panel } = Collapse;

const MATERIAL_TYPES = ["CLOTH MATERIAL", "CAP MATERIAL", "OTHER MATERIAL"];
const OTHER_MATERIAL_NAMES = ["CARD BOARD", "THREAD", "PLASTIC BAG", "CARTON"];

const columns = [
  { title: "Material Name", dataIndex: "material_name", key: "material_name" },
  { title: "Color", dataIndex: "color", key: "color" },
  { title: "Sub Type", dataIndex: "sub_type", key: "sub_type" },
  { title: "Qty Added", dataIndex: "quantity_added", key: "quantity_added" },
  { title: "Rate", dataIndex: "rate", key: "rate" },
  {
    title: "Value",
    dataIndex: "value",
    key: "value",
    render: (val) => (val != null ? `₹${Number(val).toLocaleString("en-IN")}` : "—"),
  },
  { title: "Unit", dataIndex: "unit", key: "unit" },
  { title: "Bill No", dataIndex: "bill_number", key: "bill_number" },
  { title: "Challan No", dataIndex: "challan_number", key: "challan_number" },
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    render: (val) => {
      if (!val) return "—";
      return new Date(val).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
  },
];

const csvHeaders = [
  { label: "Material Name", key: "material_name" },
  { label: "Color", key: "color" },
  { label: "Sub Type", key: "sub_type" },
  { label: "Qty Added", key: "quantity_added" },
  { label: "Rate", key: "rate" },
  { label: "Value", key: "value" },
  { label: "Unit", key: "unit" },
  { label: "Bill No", key: "bill_number" },
  { label: "Challan No", key: "challan_number" },
  { label: "Date", key: "date" },
];

const InvRepProcurement = () => {
  const [form] = Form.useForm();
  const [fieldErrors, setFieldErrors] = useState({});
  const [trigger, { data, isLoading, isFetching, error }] = useLazyFetchProcurementReportQuery();

  const handleApply = async () => {
    setFieldErrors({});
    const v = form.getFieldsValue();
    const params = {};
    if (v.date_from) params.date_from = v.date_from.format("YYYY-MM-DD");
    if (v.date_to) params.date_to = v.date_to.format("YYYY-MM-DD");
    if (v.type) params.type = v.type;
    if (v.color) params.color = v.color;
    if (v.min_rate != null) params.min_rate = v.min_rate;
    if (v.max_rate != null) params.max_rate = v.max_rate;
    if (v.other_material_name) params.other_material_name = v.other_material_name;
    const result = await trigger(params);
    if (result?.error?.status === 400 && result.error.data?.errors) {
      setFieldErrors(result.error.data.errors);
    }
  };

  const handleClear = () => {
    form.resetFields();
    setFieldErrors({});
    trigger({});
  };

  const results = data?.results || [];
  const count = data?.count ?? results.length;
  const totalQty = data?.total_quantity;
  const totalValue = data?.total_value;

  return (
    <div style={{ padding: "24px" }}>
      <h2 style={{ marginBottom: 16 }}>Procurement / Purchase Report</h2>

      <Collapse defaultActiveKey={["filters"]} style={{ marginBottom: 16 }}>
        <Panel header="Filters" key="filters">
          <Form form={form} layout="inline" style={{ flexWrap: "wrap", gap: "8px 0" }}>
            <Form.Item name="date_from" label="Date From">
              <DatePicker />
            </Form.Item>
            <Form.Item name="date_to" label="Date To">
              <DatePicker />
            </Form.Item>
            <Form.Item name="type" label="Material Type">
              <Select allowClear placeholder="All types" style={{ width: 180 }}>
                {MATERIAL_TYPES.map((t) => (
                  <Select.Option key={t} value={t}>{t}</Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="color" label="Color">
              <Input placeholder="Color" style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="min_rate" label="Min Rate">
              <InputNumber min={0} style={{ width: 110 }} />
            </Form.Item>
            <Form.Item
              name="max_rate"
              label="Max Rate"
              validateStatus={fieldErrors.max_rate ? "error" : ""}
              help={fieldErrors.max_rate}
            >
              <InputNumber min={0} style={{ width: 110 }} />
            </Form.Item>
            <Form.Item
              name="other_material_name"
              label="Other Material"
              validateStatus={fieldErrors.other_material_name ? "error" : ""}
              help={fieldErrors.other_material_name}
            >
              <Select allowClear placeholder="All" style={{ width: 160 }}>
                {OTHER_MATERIAL_NAMES.map((n) => (
                  <Select.Option key={n} value={n}>{n}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
          {error?.status === 400 && !error?.data?.errors && (
            <Alert type="error" message={error?.data?.detail || "Invalid filter values."} style={{ marginTop: 8 }} />
          )}
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <Button type="primary" onClick={handleApply} loading={isLoading || isFetching}>
              Apply Filters
            </Button>
            <Button onClick={handleClear}>Clear Filters</Button>
          </div>
        </Panel>
      </Collapse>

      {data && (
        <div
          style={{
            display: "flex",
            gap: 24,
            marginBottom: 12,
            padding: "10px 16px",
            background: "#fafafa",
            borderRadius: 6,
            border: "1px solid #e8e8e8",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <span><b>Total Records:</b> {count}</span>
          <span style={{ color: "#aaa" }}>|</span>
          <span><b>Total Quantity:</b> {totalQty ?? "—"}</span>
          <span style={{ color: "#aaa" }}>|</span>
          <span>
            <b>Total Value:</b>{" "}
            {totalValue != null ? `₹${Number(totalValue).toLocaleString("en-IN")}` : "—"}
          </span>
          {results.length > 0 && (
            <>
              <span style={{ color: "#aaa" }}>|</span>
              <Tooltip title="Export to Excel">
                <CSVLink
                  data={results}
                  headers={csvHeaders}
                  filename="procurement-report.csv"
                >
                  <Button icon={<FileExcelOutlined />} style={{ color: "#217346", borderColor: "#217346" }}>
                    Export to Excel
                  </Button>
                </CSVLink>
              </Tooltip>
            </>
          )}
        </div>
      )}

      <Spin spinning={isLoading || isFetching}>
        <Table
          columns={columns}
          dataSource={results}
          rowKey={(r, i) => r.id ?? i}
          locale={{ emptyText: "No records found" }}
          scroll={{ x: true }}
          pagination={{ pageSize: 20, showSizeChanger: true }}
        />
      </Spin>
    </div>
  );
};

export default InvRepProcurement;
